/** This import isn't used but we need it to suppress typescript errors in jsdoc. */
import { AssertionError, getPdfStandardFontFromGenericFont } from 'common';

import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Self,
  ViewChild,
  input,
  output,
} from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { PdfJsViewerComponent } from '@kariudo/ng2-pdfjs-viewer';
import * as pdfjs from '@kariudo/pdfjs/legacy/build/pdf.mjs';
import { TextItem } from '@kariudo/pdfjs/types/src/display/api';
import {
  PdfViewerComponent as NgPdfViewerComponent,
  PDFDocumentProxy,
  PDFProgressData,
} from 'ng2-pdf-viewer';
import { NGXLogger } from 'ngx-logger';
import { PDFDocument, PDFPage, StandardFonts } from 'pdf-lib';
import {
  EMPTY,
  Observable,
  catchError,
  concatMap,
  debounceTime,
  defer,
  filter,
  from,
  iif,
  last,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeLast,
  tap,
} from 'rxjs';

import { assert, assertExists, assertTypeByKey } from 'common';
import {
  DocumentAnnotations,
  DocumentAttachment,
  ImageAnnotation,
  PathAnnotation,
  PdfOptions,
  RectangleAnnotation,
  StampAnnotation,
  TextAnnotation,
  UserFriendlyError,
} from 'models';
import { NotificationService } from 'src/app/services/notification.service';

import { AppConfigQuery } from '../../app-config';
import * as utif from '../lib/utif';
import { PartialLoadData, PdfDocumentWorkload, PdfThumbnail } from '../models';
import { PdfViewerService } from '../services/pdf-viewer.service';

import html2canvas from 'html2canvas';
import { EmailData } from 'src/app/common/email-data';
import { createDataUrl } from 'src/app/common/utility';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { QpdfWasmService } from '../services/qpdf-wasm.service';
import { PartialLoadSnackbarComponent } from './partial-load-snackbar/partial-load-snackbar.component';

/** Describes an email data loaded event. */
export interface DocumentEmailDataLoadedEvent {
  /** Email metadata for the loaded email. */
  emailData: EmailData;
}

const CONVERTABLE_FILE_TYPES = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'tif',
  'eml',
  'msg',
] as const;

/**
 * File types supported for client-side PDF conversion.
 */
export type ConvertableFileType = (typeof CONVERTABLE_FILE_TYPES)[number];

/**
 * PDF Viewer.
 *
 * This component implements two different viewer libraries.
 *
 * The library enabled for use is controlled by the `usePdfJs` input boolean merged
 * with the default value from the instance configuration for `usePdfJs`.
 *
 * The PdfJs viewer offeres the mozilla PDF.js viewer in a wrapped form, that
 * provides the user a familiar interface along with a lot of out of the box
 * functionality.
 *
 * @see https://github.com/kariudo/ng2-pdfjs-viewer
 *
 * The `ng-pdf-viewer` is more stripped down and provdies custom controls and
 * implmentation.
 *
 * @see https://github.com/legalthings/angular-pdfjs-viewer
 */
@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PdfViewerService],
  standalone: false,
})
export class PdfViewerComponent implements AfterViewInit, OnDestroy, OnInit {
  /** Emits when the pdf.js after print event occurs. */
  afterPrint = output<void>();
  /** Emits when document load state changes. */
  @Output()
  documentLoadingChange = new EventEmitter<boolean>();
  /** Emits when a document is an email and its metadata has been loaded. */
  documentEmailDataLoaded = output<DocumentEmailDataLoadedEvent>();
  /** Event triggered when an error occurs while loading a document which prevents its open. */
  @Output()
  loadFailure: EventEmitter<any> = new EventEmitter();
  /** Event triggered when the page number changes. */
  @Output()
  pageChange = new EventEmitter<number>();
  /** Viewer component. */
  @ViewChild(NgPdfViewerComponent)
  pdfComponent: NgPdfViewerComponent;
  /** Viewer component for PdfJs mode. */
  @ViewChild(PdfJsViewerComponent)
  pdfJsComponent: PdfJsViewerComponent;
  /** Use PDF.js wrapped libray. */
  @Input('use-pdfjs')
  usePdfJs = this.appConfig.usePdfJs;
  /** Array of file formats supported for client conversion but that should not be used. */
  excludedClientFormats = input<ConvertableFileType[]>([]);
  /** Document Annotations. */
  documentAnnotations: DocumentAnnotations;
  /** Annotations that were burned into the document. */
  burnedAnnotations: DocumentAnnotations = [];
  /** Get if any annotations have been burned into the document. */
  get hasBurnedAnnotations() {
    return this.burnedAnnotations.length > 0;
  }
  /** Annotations loading state. */
  isAnnotationsLoading = false;
  /** True while the PDF.js viewer is processing a document, indicates it should be hidden. */
  isDocumentLoading = false;
  /** Get if the document is encrypted. */
  get isDocumentEncrypted() {
    return this.workload?.document.isEncrypted ?? false;
  }
  /** Loading progress percentage (0-100). */
  loadingProgressPercentage = 0;
  /**
   * Partial document is currently displayed.
   *
   * This is used to inform the user that we have not loaded the full document.
   */
  partialDocumentDisplayed = false;
  /** State of a partially loaded document. */
  partialLoadState?: PartialLoadData;
  /** Byte array for the PDF loaded. */
  pdfBytes: Uint8Array;
  /** Observable PDF byte array. */
  pdfSrc$: Observable<Uint8Array>;
  /** Currently reloading a complete version of the document after a partial load. */
  reloadingCompleteDocument = false;
  /**
   * Emits when the viewer zoom scale changes.
   *
   * This currently only emits  when using the pdfjs viewer component.
   */
  scaleChanged = output<number>();
  /** Array of thumbnails for the document. */
  thumbnails: PdfThumbnail[] = [];
  /** User's default zoom setting. */
  userDefaultZoom: string = this.appQuery.defaultPageZoom;

  private partialLoadSnackbarReference?: MatSnackBarRef<PartialLoadSnackbarComponent>;
  private pdfWorker: pdfjs.PDFWorker;
  /** Workload created with the viewed document. */
  private workload: PdfDocumentWorkload;

  constructor(
    @Self() private pdfViewerService: PdfViewerService,
    private logger: NGXLogger,
    private appConfig: AppConfigQuery,
    private httpClient: HttpClient,
    private notify: NotificationService,
    private translate: TranslocoService,
    private changeDetectorReference: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private appQuery: ApplicationQuery,
    private qpdfWasmService: QpdfWasmService
  ) {
    // Set our worker source.
    pdfjs.GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.mjs';
    // Init a single pdf worker for this instance of pdf viewer.
    this.pdfWorker = new pdfjs.PDFWorker();
  }

  /**
   * Check if a file extension is client convertable.
   *
   * Case and `.` will be ignored.
   *
   * @param fileExtension File extension.
   * @param allowMsgClientConversion Whether to allow MSG files to be converted client side, controlled by user setting.
   * @returns True if the client can convert this format to a PDF for display.
   */
  static isClientConvertableExtension(
    fileExtension: string,
    excludedFormats: ConvertableFileType[] = []
  ): boolean {
    const extensions = CONVERTABLE_FILE_TYPES.filter(
      (x) => !excludedFormats.includes(x)
    );
    return extensions.includes(
      fileExtension.replace('.', '').toLowerCase() as ConvertableFileType
    );
  }

  /**
   * Add a new page to a PDF with an embedded image.
   *
   * @param pdf The PDF to add the page to.
   * @param pdfConvertableBytes Image bytes (`ArrayBuffer` or Data URL `string`).
   * @param filetype File type of the image.
   * @returns The observable `PDFDocument` with embedded image.
   */
  private static addPageOfImage(
    pdf: PDFDocument,
    pdfConvertableBytes: ArrayBuffer | string,
    filetype: 'png' | 'jpg'
  ): Observable<PDFDocument> {
    const page = pdf.addPage();
    const embedPromise =
      filetype === 'png'
        ? pdf.embedPng(pdfConvertableBytes)
        : pdf.embedJpg(pdfConvertableBytes);
    return from(embedPromise).pipe(
      tap((pdfImage) => {
        // Set the new page size to match the image.
        page.setSize(pdfImage.width, pdfImage.height);
        // Draw the image to the new page.
        page.drawImage(pdfImage);
      }),
      // Return the obserable of the new PDF.
      map(() => pdf)
    );
  }
  /**
   * Is the URL convertable to PDF.
   *
   * @param url Url to check.
   * @returns Convertable file extension (i.e.; "png").
   */
  private getUrlPdfConvertableType(
    url: string
  ): ConvertableFileType | undefined {
    const convertableQueryParameterValue = /&clientConvert=([^&]+)/
      .exec(url)?.[1]
      .toLowerCase();
    if (!convertableQueryParameterValue) return;
    assert(
      PdfViewerComponent.isClientConvertableExtension(
        convertableQueryParameterValue,
        this.excludedClientFormats()
      )
    );
    return convertableQueryParameterValue as ConvertableFileType;
  }

  /**
   * Is the byte array a PDF.
   *
   * Checks in the first 1024 bytes for the PDF format header "%PDF".
   *
   * @param arrayBuffer Buffer to test.
   * @returns True if the byte array contains a PDF.
   */
  private static isPdfArrayBuffer(arrayBuffer: ArrayBuffer): boolean {
    // Get up to the first 1024 bytes in the buffer.
    const bytes = new Uint8Array(
      arrayBuffer.slice(0, Math.min(1024, arrayBuffer.byteLength))
    );
    if (bytes?.length < 4) return false;
    for (let index = 0; index < bytes.length; index++)
      if (
        bytes[index] === '%'.charCodeAt(0) &&
        bytes[index + 1] === 'P'.charCodeAt(0) &&
        bytes[index + 2] === 'D'.charCodeAt(0) &&
        bytes[index + 3] === 'F'.charCodeAt(0)
      )
        return true;
    return false;
  }

  /**
   * Is the URL convertable to PDF.
   *
   * @param url Url to check.
   * @returns True if the url file type can be converted to PDF.
   */
  private isUrlPdfConvertable(url: string): boolean {
    return !!this.getUrlPdfConvertableType(url);
  }

  /**
   * Runs find in document using the provided search text.
   *
   * @param searchText Search text.
   * @param caseSenstive Match case.
   * @param hightlightAll Highlight all matches.
   * @param wholeWord Consider the whole word.
   */
  findInDocument(
    searchText: string,
    caseSenstive: boolean,
    hightlightAll: boolean,
    wholeWord: boolean
  ): void {
    assert(this.usePdfJs, 'PDFjs must be used to run find in document.');
    this.pdfJsComponent.PDFViewerApplication.findBar.open();
    this.pdfJsComponent.PDFViewerApplication.findBar.findField.value =
      searchText;
    this.pdfJsComponent.PDFViewerApplication.findBar.caseSensitive.checked =
      caseSenstive;
    this.pdfJsComponent.PDFViewerApplication.findBar.highlightAll.checked =
      hightlightAll;
    this.pdfJsComponent.PDFViewerApplication.findBar.entireWord.checked =
      wholeWord;
    this.pdfJsComponent.PDFViewerApplication.findBar.findNextButton.click();
  }

  /**
   * Get page words.
   *
   * @param pageNumber - Page number.
   * @param canvasWidth - Canvas width.
   * @returns - Observable of page.
   */
  getPageWords(pageNumber: number, canvasWidth: number): Observable<any> {
    // Make a copy because pdf.js flushes the bytes when running getDocument.
    const pdfBytes = new Uint8Array(this.pdfBytes);
    const document = from(
      pdfjs.getDocument({ data: pdfBytes, worker: this.pdfWorker }).promise
    );
    return document.pipe(
      switchMap((document) => from(document.getPage(pageNumber))),
      switchMap((page) =>
        from(
          page.getTextContent({
            keepWhiteSpace: true,
            includeTextContentChars: true,
          } as any)
        ).pipe(
          map((content) => {
            const words = [];
            for (const item of content.items) {
              assertTypeByKey<TextItem>(item, 'height', 'number');
              if (!item.chars) continue;
              const height = page.getViewport({ scale: 1 }).viewBox[3],
                width = page.getViewport({ scale: 1 }).viewBox[2],
                scale = canvasWidth / width,
                wordY =
                  (height - Number(item.transform[5]) - item.height) * scale,
                wordHeight = item.height;
              // If a split occurs, we figure out x-coordinates and widths after splitting the string up.
              const splitWords = item.str.split(' ');
              if (splitWords.length > 1) {
                const spaceWidth = this.getSpaceWidth(item);
                let wordX = item.transform[4];
                for (const index in splitWords) {
                  //Calculate word width.
                  let wordWidth = 0;
                  for (const char of [...splitWords[index]]) {
                    const charWidth = item.chars.find(
                      (c) => c.unicode === char
                    )?.width;
                    if (!charWidth) break;
                    wordWidth += charWidth;
                  }
                  const newWord: any = {
                    text: splitWords[index],
                    height: wordHeight * scale,
                    fontName: content.styles[item.fontName],
                    width: wordWidth * scale,
                    x: wordX * scale,
                    y: wordY,
                  };
                  words.push(newWord);
                  //Update x-coordinate for the next word.
                  wordX = splitWords[Number(index) + 1]
                    ? wordX + wordWidth + spaceWidth
                    : wordX + wordWidth;
                  wordWidth = 0;
                }
              } else {
                // No split. Use word with no manipulation.
                const word: any = {
                  text: item.str,
                  height: item.height * scale,
                  fontName: content.styles[item.fontName],
                  width: item.width * scale,
                  x: item.transform[4] * scale,
                  y: (height - Number(item.transform[5]) - item.height) * scale,
                };
                words.push(word);
              }
            }
            return words;
          })
        )
      )
    );
  }

  ngAfterViewInit(): void {
    // Register the PDF component with the service.
    try {
      assertExists(this.pdfComponent);
      this.pdfViewerService.registerPdfComponent(this.pdfComponent);
    } catch {
      assertExists(this.pdfJsComponent);
      this.fixPdfJsIframeRendering();
      this.pdfJsComponent.onPageChange
        .pipe(debounceTime(1))
        .subscribe((newPage) => (this.pageNumber = newPage));
    }
  }

  ngOnDestroy(): void {
    // Close the partial load snackbar if it is still open.
    if (this.partialLoadSnackbarReference) {
      this.partialLoadSnackbarReference.dismiss();
    }
  }

  ngOnInit(): void {
    this.pdfSrc$ = this.createPdfBytesFromPdfUrl$();
  }

  /** PDF options. */
  @Input('pdf-options')
  set pdfOptions(value: PdfOptions) {
    // Set the annotations first, so they can be checked during the URL pipeline.
    this.documentAnnotations = value.annotations;
    this.pdfViewerService.url = value.url;
  }

  /**
   * Gets the content window for the PDF JS document.
   *
   * @returns The content window.
   * @throws {AssertionError} If the page element does not exist or if you are not using the ng2-pdfjs-viewer module.
   */
  get contentWindow(): Window {
    assert(
      this.usePdfJs,
      'Only the ng2-pdfjs-viewer module is supported at this time.'
    );
    const contentWindow = this.pdfJsComponent.iframe.nativeElement
      .contentWindow as Window | undefined;
    assertExists(contentWindow, 'Content window must exist.');
    return contentWindow;
  }

  /**
   * Gets the page element for the current page.
   *
   * @returns An HTMLDivElement for the current page.
   * @throws {AssertionError} If the page element does not exist or if you are not using the ng2-pdfjs-viewer module.
   */
  get pageElement(): HTMLDivElement {
    assert(
      this.usePdfJs,
      'Only the ng2-pdfjs-viewer module is supported at this time.'
    );
    const pageElement =
      this.pdfJsComponent.iframe.nativeElement.contentWindow.document.querySelector(
        '.pdfViewer [data-page-number="' + this.pageNumber + '"]'
      ) as HTMLDivElement | undefined;
    assertExists(pageElement, 'Page element must exist.');
    return pageElement;
  }

  get pageElement2(): HTMLDivElement | undefined {
    assert(
      this.usePdfJs,
      'Only the ng2-pdfjs-viewer module is supported at this time.'
    );
    const pageElement =
      this.pdfJsComponent.iframe.nativeElement.contentWindow.document.querySelector(
        '.pdfViewer [data-page-number="' + this.pageNumber + '"]'
      ) as HTMLDivElement | undefined;
    return pageElement;
  }
  /**
   * Get the current page page number, tracked in service.
   *
   * @returns Page number.
   */
  get pageNumber() {
    return this.pdfViewerService.pageNumber;
  }

  /**
   * Update the current page number, tracked in service.
   *
   * @param value New page number.
   */
  set pageNumber(value: number) {
    this.pdfViewerService.pageNumber = value;
    this.pageChange.emit(value);
  }

  /** Get the current user requested zoom scale.
   *
   * @returns Zoom scale value.
   */
  get zoomScale() {
    return this.pdfViewerService.zoomScale;
  }

  /**
   * Go to a specified page in the document.
   *
   * @param pageNumber Page number.
   */
  gotToPage(pageNumber: number): void {
    if (this.usePdfJs) {
      this.pdfJsComponent.page = pageNumber;
    } else {
      this.pdfComponent.page = pageNumber;
      // This component doesn't appear to have any eventing that syncs the viewer's page number with this component
      // so manually set the page here.
      this.pageNumber = pageNumber;
    }
  }

  /**
   * Event handler for click of Load Complete Document button.
   */
  onClickLoadCompleteDocument(): void {
    this.partialDocumentDisplayed = false;
    this.reloadingCompleteDocument = true;
    // Invoke URL setter to reload.
    this.pdfViewerService.url = this.pdfViewerService.url;
  }

  /**
   * Event handler for completetion of PDF load.
   *
   * Only used by basic viewer mode, Advanced viewer has its own, @see onPdfJsDocumentLoad.
   *
   * @param pdf Loaded PDF document.
   */
  onLoadComplete(pdf: PDFDocumentProxy): void {
    this.reloadingCompleteDocument = false;
    this.pdfViewerService.pageCount = pdf.numPages;
    this.setIsDocumentLoading(false);
    this.loadingProgressPercentage = 100; // Ensure completed.
    this.pageNumber = 1;
    this.logger.debug('PDF preview loaded.', pdf);
  }

  /**
   * Handler for the pdf page change event.
   *
   * @param pageNumber New page number.
   */
  onPageChange(pageNumber: number): void {
    this.logger.debug('pdfjs component page changed: ', pageNumber);
    this.pageNumber = pageNumber;
  }

  /**
   * Event handler for after PdfJs loads.
   */
  onPdfJsDocumentLoad(): void {
    this.reloadingCompleteDocument = false;
    this.fixPdfJsApplicationRendering();
    this.pageNumber = 1;
    // Try to force the viewer to load the first page because the viewer sometimes loads the second or third page of certain documents.
    setTimeout(() => (this.pdfJsComponent.page = 1));
    this.setIsDocumentLoading(false);
    this.changeDetectorReference.detectChanges();
    if (this.partialDocumentDisplayed) {
      this.partialLoadSnackbarReference = this.snackBar.openFromComponent(
        PartialLoadSnackbarComponent,
        {
          data: this.partialLoadState,
          horizontalPosition: 'end',
        }
      );
      this.partialLoadSnackbarReference
        .onAction()
        .subscribe(() => this.onClickLoadCompleteDocument());
    }
  }

  /**
   * Handles the PdfJsComponent scale change event.
   *
   * @param newScaleFactor The new scale factor emitted by the PdfJsComponent.
   */
  onPdfJsDocumentScaleChange(newScaleFactor: number): void {
    this.scaleChanged.emit(newScaleFactor);
  }

  /**
   * Event handler for loading progress of PDF.
   *
   * @param progressData PDF progress data.
   */
  onProgress(progressData: PDFProgressData): void {
    this.loadingProgressPercentage = Math.round(
      (progressData.loaded / progressData.total) * 100
    );
    this.logger.debug('PDF load progress.', progressData);
  }

  /**
   * Add a new page to a PDF indicating the document was partially converted.
   *
   * @param pdf The PDF to add the page to.
   * @param partialLoadData The state of partial load.
   * @returns The observable `PDFDocument` with appended page.
   */
  private addPartialDocumentPage(
    pdf: PDFDocument,
    partialLoadData: PartialLoadData
  ): Observable<PDFDocument> {
    const page = pdf.addPage();
    const translatedMessage$ = this.translate.selectTranslate(
      'PARTIALLY_CONVERTED_PDF_PAGE_MSG',
      partialLoadData
    );
    const pdfWithNewPage$ = translatedMessage$.pipe(
      map((text: string) => {
        page.setSize(600, 90);
        page.drawText(text, {
          size: 20,
          x: 30,
          y: 48,
          maxWidth: 540,
        });
        return pdf;
      })
    );
    return pdfWithNewPage$;
  }

  /**
   * Burns annotations into the document where burn is set to true.
   *
   * This method was converted from the original read only write of all supported annotations
   * to the user editable canvas support, and now is used to burn requested annotations to the document.
   *
   * @param workload Workload to apply annotations to. If not provided, the current workload is used.
   */
  async burnAnnotations(workload?: PdfDocumentWorkload): Promise<void> {
    // Get the pages.
    const pages =
      workload?.document.getPages() ?? this.workload?.document.getPages();
    assertExists(pages, 'Workload contains no pages.');

    const annotationsForBurn = this.documentAnnotations.filter((a) => a.burn);

    // If there are annotations to burn, and the document is encrypted, error to
    // prevent corruption and abort the document save flow.
    if (annotationsForBurn.length > 0 && this.workload.document.isEncrypted) {
      throw new UserFriendlyError(
        'Document is encrypted, cannot burn annotations.',
        'Burning annotations would corrupt the encrypted document.',
        'PDF_DOCUMENT_ENCRYPTED_NO_BURN'
      );
    }

    // Loop and apply each burn annotation.
    for (const annotation of annotationsForBurn) {
      // If in a partial load (conversion) state:
      if (this.partialDocumentDisplayed) {
        assertExists(this.partialLoadState);
        // Skip any annotations on pages greater than those included.
        if (annotation.page >= this.partialLoadState.loaded) continue;
      }
      // Get the page the annotation should be applied to.
      let page: PDFPage;
      try {
        page = pages[annotation.page];
        assertExists(page, 'Page does not exist.');
      } catch {
        // If we can't obtain the index that corresponds to the annotations
        // page, then something is wrong with that annotation or worse,
        // report an error and continue to the next annoation.
        this.logger.error(
          'Annotation page could not be obtained in PDF document.'
        );
        continue;
      }
      // Associate the embedded font if required.
      if (annotation.hasFont) {
        assert(annotation instanceof TextAnnotation);
        annotation.embedFont(this.workload.embeddedFonts[annotation.font.name]);
      }
      // Set the scale factor for annotation cooridanate adjustment.
      const pageScale =
        typeof this.workload.annotationScale === 'number'
          ? this.workload.annotationScale
          : this.workload.annotationScale[annotation.page];
      if (pageScale) annotation.scaleFactor = pageScale;

      // If the annotation requires an embedded image, ensure it is available before attempting draw.
      if (
        annotation instanceof ImageAnnotation ||
        annotation instanceof StampAnnotation ||
        annotation instanceof PathAnnotation
      ) {
        if (!annotation.isImageEmbedded) {
          this.logger.debug('Embedding annotation as image in PDF.');
          // Render the annotation as an image.
          const imageDataUri = annotation.toPng();
          // Embed the image in the document.
          const image = await this.workload.document.embedPng(imageDataUri);
          // Update the annotation data.
          annotation.embedImage(image);
        }
      }

      // Highlight annotations should not have borders, when burned.
      if (annotation instanceof RectangleAnnotation && annotation.isHighlight) {
        annotation.border.thickness = 0;
      }

      // If the annotation requires an embedded font, ensure it is available before attempting draw.
      if (annotation instanceof TextAnnotation) {
        const standardFont = getPdfStandardFontFromGenericFont(
          annotation.font.name
        );
        if (!this.workload.embeddedFonts[standardFont]) {
          this.logger.debug('Embedding annotation as font in PDF.');
          // Embed the font in the document.
          const font = await this.workload.document.embedFont(
            getPdfStandardFontFromGenericFont(annotation.font.name)
          );
          // Update the annotation data.
          this.workload.embeddedFonts[standardFont] = font;
          annotation.embedFont(font);
        }
      }

      // Draw the annotation on the page.
      annotation.drawOnPage(page, true, true);
      // Add the annotation to the burned annotations list.
      this.burnedAnnotations.push(annotation);
      // Remove the burned annotation from the list.
      this.documentAnnotations = this.documentAnnotations.filter(
        (a) => a !== annotation
      );
    }
  }

  /**
   * Save any changes made to the PDF document such as burning annotations.
   *
   * @returns An observable that emits when the PDF changes have finished saving.
   */
  saveLocalPdfChanges(): Observable<void> {
    return defer(async () => {
      this.logger.debug('Saving PDF with burned annotations.');
      // Save the document with any changes that have been made to the workload document.
      const pdfBytes = await this.workload.document.save();
      // Save the new PDF bytes immediately to the exposed viewer bytes to allow things like saving to get them without waiting for the url$ to emit.
      this.pdfBytes = pdfBytes;
      const dataUrl = createDataUrl(pdfBytes);
      this.pdfViewerService.url = dataUrl;
    });
  }

  /**
   * Show annotations loading progress bar, and toast if applicable.
   * This will hide the viewer itself while loading as well.
   *
   * @returns Function to dismiss loading toast.
   */
  private beginLoadingProgress() {
    this.isAnnotationsLoading = true;
    this.setIsDocumentLoading(true);
    this.changeDetectorReference.detectChanges();
    // Reset progress bar.
    this.loadingProgressPercentage = 0;
    // Reset partial document state to fresh.
    this.partialDocumentDisplayed = false;
    this.partialLoadState = undefined;
    if (this.partialLoadSnackbarReference instanceof MatSnackBarRef) {
      this.partialLoadSnackbarReference.dismiss();
    }
  }

  /**
   * Complete the loading progress state.
   *
   */
  private completeLoadingProgress() {
    this.logger.debug('Document annotation modificaton complete.');
    // Switch from showing progress bar to showing then pdf viewer.
    this.isAnnotationsLoading = false;
  }

  private async renderElementToPng(element: HTMLElement): Promise<string> {
    const renderScale = Math.max(window.devicePixelRatio || 1, 2);
    const canvas = await html2canvas(element, {
      scale: renderScale,
      useCORS: true,
    });
    return canvas.toDataURL('image/png');
  }

  private convertEmlToPdf(
    pdf: PDFDocument,
    pdfConvertableBytes: ArrayBuffer
  ): Observable<PdfDocumentWorkload> {
    return defer(async () => {
      const emailData = await EmailData.fromEml(pdfConvertableBytes);

      this.documentEmailDataLoaded.emit({ emailData });
      const emailElement = emailData.toHtml();

      document.body.appendChild(emailElement);
      const pngUrl = await this.renderElementToPng(emailElement);
      const page = pdf.addPage();
      const pdfImage = await pdf.embedPng(pngUrl);
      document.body.removeChild(emailElement);
      page.setSize(pdfImage.width, pdfImage.height);
      page.drawImage(pdfImage);
      return new PdfDocumentWorkload(pdf);
    });
  }

  private convertMsgToPdf(
    pdf: PDFDocument,
    pdfConvertableBytes: ArrayBuffer
  ): Observable<PdfDocumentWorkload> {
    return defer(async () => {
      const emailData = await EmailData.fromMsg(
        pdfConvertableBytes,
        this.translate,
        this.logger
      );

      this.documentEmailDataLoaded.emit({ emailData });
      const emailElement = emailData.toHtml({
        imageLoadWarning: this.translate.translate(
          'MSG_EMAIL_IMAGE_NOT_LOADED'
        ),
      });

      document.body.appendChild(emailElement);
      const pngUrl = await this.renderElementToPng(emailElement);
      document.body.removeChild(emailElement);
      const page = pdf.addPage();
      const pdfImage = await pdf.embedPng(pngUrl);
      page.setSize(pdfImage.width, pdfImage.height);
      page.drawImage(pdfImage);
      return new PdfDocumentWorkload(pdf);
    });
  }

  private convertTiffFrameToPng(
    tiffBytes: ArrayBuffer,
    frame: utif.Ifd
  ): { dataUrl: string; scale: number } {
    // Decode the target frame, generates the width, height, and inflated
    // data for the frame in the ifd object.
    utif.decodeImage(tiffBytes, frame);
    // Note: The t282 property is the XResolution, which we can infer as the DPI.
    // If we need to use this to offset against relative scale etc.
    // const dpi: number = frame.t282 ? (frame.t282 as any)[0][0] : 72;
    // Get pixel byte array.
    const rbga = utif.toRGBA8(frame);
    // Create and configure a new canvas.
    let canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const context = canvas.getContext('2d');
    assertExists(context);
    // Create a new ImageData object.
    const imageData = context.createImageData(frame.width, frame.height);
    // Fill the image data with the decoded frame bytes.
    imageData.data.set(rbga);
    // Add the image to the canvas.
    context.putImageData(imageData, 0, 0);
    // If the image is larger than our "max size box", scale it.
    const { scale, scaledCanvas } = this.restrictCanvasToMaxSize(
      frame,
      canvas,
      1200
    );
    // Apply restricted canvas.
    canvas = scaledCanvas;
    // Collect the data URL from the rendered ImageData.
    // Add the PNG to the array to be embedded, and scale.
    return { dataUrl: canvas.toDataURL(), scale };
  }

  /**
   * Convert a TIFF to embedded PDF pages (as PNG).
   *
   * Warning: It could cause a problem here for memory consumption
   * if there are a lot of pages.
   * We may need to add limitations here to force a bailout.
   *
   * @todo Add a TIFF specific progress bar, it can be determinate since we know.
   *
   * @param tiffBytes Tiff byte buffer to convert and embed.
   * @param pdfDocument PDFDocument to embed images in.
   * @param limitPagesLoaded Limit the pages loaded to speed up load time. (0 is unlimited, default 5).
   * @returns PDF document with images embedded.
   */
  private convertTiffToEmbeddedPdfPages(
    tiffBytes: ArrayBuffer,
    pdfDocument: PDFDocument,
    limitPagesLoaded: number = 5
  ) {
    const scales: number[] = [];
    // Decode TIFF container data.
    const ifds = utif.decode(tiffBytes);
    this.logger.debug(`Decoded TIFF container with ${ifds.length} pages.`);
    // For each frame, decode, rescale, and convert to data URL with canvas.
    const framesToDecode$ = limitPagesLoaded
      ? from(ifds).pipe(take(limitPagesLoaded))
      : from(ifds);
    return framesToDecode$.pipe(
      map((frame, index) => {
        this.logger.debug(`Decoded TIFF page ${index + 1} of ${ifds.length}.`);
        var result = this.convertTiffFrameToPng(tiffBytes, frame);
        scales[index] = result.scale;
        return result.dataUrl;
      }),
      tap(() => {
        // Determine if we are displaying a partial document.
        this.partialDocumentDisplayed =
          limitPagesLoaded !== 0 && limitPagesLoaded < ifds.length;
        // Update partial load state, clear it if load was complete.
        this.partialLoadState = this.partialDocumentDisplayed
          ? {
              loaded: limitPagesLoaded,
              total: ifds.length,
            }
          : undefined;
      }),
      // Embed each (possibly scaled) image as a new page.
      concatMap((data) => {
        return PdfViewerComponent.addPageOfImage(pdfDocument, data, 'png');
      }),
      // Once all data URL are added, just emit the final PDF.
      last(),
      switchMap(() => {
        // If this was a partial load, add a page with explanation before returing the PDF.
        if (this.partialDocumentDisplayed) {
          assertExists(this.partialLoadState);
          return this.addPartialDocumentPage(
            pdfDocument,
            this.partialLoadState
          );
        }
        // Otherwise just return the PDF as is.
        return of(pdfDocument);
      }),
      tap(() => {
        this.logger.debug(
          'Completed processing all TIFF frames to PNG, embedded.'
        );
      }),
      // Include any scales in the return.
      map(() => ({ pdfDocument, scales }))
    );
  }

  /**
   * Create a PDF from an image byte array.
   *
   * @param arrayBuffer$ Observable `ArrayBuffer` of support image.
   * @param type Image type for conversion.
   * @returns Observable `PDFDocument` of the image.
   */
  private createNewPdfFromConvertable(
    arrayBuffer$: Observable<ArrayBuffer>,
    type: ConvertableFileType
  ): Observable<PdfDocumentWorkload> {
    return arrayBuffer$.pipe(
      // Create a PDF document object from the fetched byte data.
      switchMap((pdfConvertableBytes: ArrayBuffer) => {
        assertExists(type);
        this.logger.debug(
          `Converting ${type.toUpperCase()} to PDF.`,
          pdfConvertableBytes
        );
        const pdf$ = from(PDFDocument.create()).pipe(
          switchMap((pdf) => {
            let pdfDocumentWorkload$: Observable<PdfDocumentWorkload>;
            // Process the conversion and embed according to file type.
            switch (type) {
              case 'msg':
                pdfDocumentWorkload$ = this.convertMsgToPdf(
                  pdf,
                  pdfConvertableBytes
                );
                break;
              case 'eml':
                pdfDocumentWorkload$ = this.convertEmlToPdf(
                  pdf,
                  pdfConvertableBytes
                );
                break;
              case 'png':
                // Embed the PNG to a new PDF page.
                pdfDocumentWorkload$ = PdfViewerComponent.addPageOfImage(
                  pdf,
                  pdfConvertableBytes,
                  'png'
                ).pipe(
                  map((pdfDocument) => new PdfDocumentWorkload(pdfDocument))
                );
                break;
              case 'jpg':
              case 'jpeg':
                // Embed the JPEG to a new PDF page.
                pdfDocumentWorkload$ = PdfViewerComponent.addPageOfImage(
                  pdf,
                  pdfConvertableBytes,
                  'jpg'
                ).pipe(
                  map((pdfDocument) => new PdfDocumentWorkload(pdfDocument))
                );
                break;
              case 'tif':
              case 'tiff':
                // Decode TIFF image data.
                pdfDocumentWorkload$ = this.convertTiffToEmbeddedPdfPages(
                  pdfConvertableBytes,
                  pdf,
                  // If the document is being reloaded on a request for complete, remove page load limit.
                  this.reloadingCompleteDocument ? 0 : undefined
                ).pipe(
                  map(
                    ({ pdfDocument, scales }) =>
                      new PdfDocumentWorkload(pdfDocument, undefined, scales)
                  )
                );
                break;
              default:
                // TODO: may need to throw for error handling here instead of empty?
                this.logger.error('Unsupported converstion to PDF.');
                return EMPTY;
            }
            return pdfDocumentWorkload$;
          })
        );
        return pdf$;
      })
    );
  }

  /**
   * Embed any required fonts in a `PDFDocumentWorkload`.
   *
   * @todo Add support for burnable annotations limiting what is embedded.
   *
   * @param workload Workload.
   * @returns Workload, with any required fonts embedded.
   */
  private embedAllRequiredFonts(
    workload: PdfDocumentWorkload
  ): Observable<PdfDocumentWorkload> {
    // Scan the list of annotations to determine what unqiue fonts we need.
    const allRequiredFonts = new Set(
      this.documentAnnotations
        .filter((a) => a.hasFont)
        .flatMap((a) => (a as TextAnnotation).requiredFonts)
    );
    // If there are annotations that require fonts.
    if (allRequiredFonts.size > 0) {
      // Embed fonts.
      return from(allRequiredFonts).pipe(
        mergeMap((font) => this.embedFontInWorkflow(workload, font)),
        takeLast(1)
      );
    } else {
      // No fonts needed, return the workload observable.
      return of(workload);
    }
  }

  /**
   * Embed any required images in a `PDFDocumentWorkload`.
   *
   * @todo Add support for burnable annotations limiting what is embedded.
   *
   * @param workload Workload.
   * @returns Workload, with any required images embedded.
   */
  private embedAllRequiredImages(
    workload: PdfDocumentWorkload
  ): Observable<PdfDocumentWorkload> {
    // Scan the list of annotations to determine if images need to be embedded.
    const annotationsWithImages = this.documentAnnotations.filter(
      (a) =>
        a instanceof ImageAnnotation ||
        a instanceof StampAnnotation ||
        a instanceof PathAnnotation
    ) as (ImageAnnotation | StampAnnotation)[];
    // If there are annotations that require images.
    if (annotationsWithImages.length > 0) {
      // Embed images in PDF.
      return from(annotationsWithImages).pipe(
        // Embed each image.
        mergeMap((annotationWithImage) =>
          this.embedImageInWorkflow(workload, annotationWithImage)
        ),
        // Emit the workload when done.
        takeLast(1)
      );
    } else {
      // No images needed, return the workload observable.
      return of(workload);
    }
  }

  /**
   * Embed a font for an annotation into the PDF document.
   *
   * @param workload PDF document workload.
   * @param font Font to embed.
   * @returns The observable PDF document workload.
   */
  private embedFontInWorkflow(
    workload: PdfDocumentWorkload,
    font: StandardFonts
  ) {
    return from(workload.document.embedFont(font)).pipe(
      map((pdfFont) => {
        workload.embeddedFonts[font] = pdfFont;
        return workload;
      })
    );
  }

  /**
   * Embed a PNG image for an annotation into the PDF document.
   *
   * @param workload PDF document workload.
   * @param imageAnnotation Annotation with image.
   * @returns The observable PDF document workload.
   */
  private embedImageInWorkflow(
    workload: PdfDocumentWorkload,
    imageAnnotation: ImageAnnotation | StampAnnotation | PathAnnotation
  ) {
    const pngDataUri = imageAnnotation.toPng();
    const embedPromise = workload.document.embedPng(pngDataUri);
    return from(embedPromise).pipe(
      map((pdfImage) => {
        this.logger.debug('Embedding image in PDF.');
        imageAnnotation.embedImage(pdfImage);
        return workload;
      }),
      catchError((error) => {
        this.logger.error('Error while embedding image.', error);
        return EMPTY;
      })
    );
  }

  /**
   * Fixes for how the PdfJs wrapped application renders.
   */
  private fixPdfJsApplicationRendering() {
    const viewerConfig = this.pdfJsComponent.PDFViewerApplication.appConfig;
    // Watch the viewer's iframe window for the after print event.
    const iframeWindow = this.pdfJsComponent.iframe.nativeElement.contentWindow;
    if (iframeWindow) {
      iframeWindow.addEventListener('afterprint', () => {
        this.afterPrint.emit();
      });
    }
    // Remove horizontal scrollbar from thumbnailer.
    viewerConfig.sidebar.thumbnailView.style.overflowX = 'hidden';
  }

  /** Fixes for how the `<iframe>` element is presented from PdfJs library. */
  private fixPdfJsIframeRendering() {
    const iframeElement = this.pdfJsComponent.iframe.nativeElement;
    // Hide the frame border.
    iframeElement.frameBorder = 0;
  }

  /**
   * Gets the size of a space in from the passed item.
   *
   * @param item - Text content item.
   * @returns - Space size.
   */
  private getSpaceWidth(item: any) {
    let charTotal = 0;
    for (const charI in item.chars) {
      charTotal += item.chars[charI].width;
    }
    const difference = item.width - charTotal;
    /**
     * If the width is 0 then that means spaces are included in the chars array,
     * if the width is less than 1 the spaces are probably included however commas may exist
     * on the line, and we can get the width of spaces from there.
     */
    const spaceWidth =
      difference > 1
        ? difference / (item.str.split(' ').length - 1)
        : item.chars.find((c: any) => c.unicode === ' ')?.width;
    return spaceWidth;
  }

  private createPdfBytesFromPdfUrl$(): Observable<Uint8Array> {
    return this.pdfViewerService.url$.pipe(
      tap(() => this.resetViewerModifiedState()),
      filter((url) => !!url),
      switchMap((url) => {
        // Ensure that the partial load snackbar is dismissed since the document is changing.
        this.partialLoadSnackbarReference?.dismiss();

        // Show loading progress bar and toast as required.
        this.beginLoadingProgress();

        // Get the PDF bytes from the URL.
        const originalBytes$ = this.getBytesFromUrl(url);

        // Load a PDFDocument from the existing PDF URL to bytes as Observable.
        const pdfDocument$ = this.loadAsPdf(originalBytes$, url);

        // Perform any initial modifcations to the document before display.
        // This includes embedding fonts, images, and burned annotations.
        const pdfBytes$ = pdfDocument$.pipe(
          // If the document does not appear to provide a page count, attempt to reprocess it with qpdf-wasm.
          // If the document is encrypted skip this check because it will throw an error.
          switchMap((workload) => {
            if (
              !workload.document.isEncrypted &&
              workload.document.getPageCount() === 0
            ) {
              return this.reprocessPdfWithQpdfWasm(originalBytes$, workload);
            }
            return of(workload);
          }),
          // Store the workload.
          tap((workload) => (this.workload = workload)),
          // Only allow modify of the document if it is not encrypted (this includes signatures).
          // If the document is encrypted, just return the original bytes.
          mergeMap((workload) =>
            iif(
              () =>
                !workload.document.isEncrypted && // Ensure document is not encrypted.
                workload.document.getPageCount() > 0, // Ensure PDFLib can parse pages.
              of(workload as PdfDocumentWorkload).pipe(
                // Embed any required fonts.
                switchMap((workload) => this.embedAllRequiredFonts(workload)),
                // Embed any images.
                switchMap((workload) => this.embedAllRequiredImages(workload)),
                // Burn annotations as needed.
                tap(() => this.burnAnnotations()),
                // Save the changes to the PDF document, and return the bytes.
                switchMap((workload) => this.saveWorkloadToBytes(workload))
              ),
              // If we can't modify the document or parse it with PDFLib, return the original bytes.
              originalBytes$.pipe(map((bytes) => new Uint8Array(bytes)))
            )
          ),
          // Hide annotation loading progress bar.
          tap(() => {
            this.completeLoadingProgress();
          })
        );

        // Return the result.
        return pdfBytes$.pipe(
          tap((bytes) => {
            // PdfJs does not react to the URL changing with a refresh, queue a
            // refresh to occur by force after the URL is updated.
            if (this.usePdfJs && this.pdfJsComponent) {
              const refreshWhenSet = setInterval(() => {
                if (bytes === this.pdfJsComponent.pdfSrc) {
                  this.pdfJsComponent.refresh();
                  clearInterval(refreshWhenSet);
                }
              }, 10);
            }
          })
        );
      })
    );
  }

  /**
   * Reprocess a PDF with qpdf-wasm to attempt to recover pages.
   *
   * @param originalBytes$ Original PDF bytes
   * @param workload Current workload object
   */
  reprocessPdfWithQpdfWasm(
    originalBytes$: Observable<ArrayBuffer>,
    workload: PdfDocumentWorkload
  ): Observable<PdfDocumentWorkload> {
    this.logger.debug(
      'Document has no pages, attempting to reprocess with qpdf-wasm.'
    );
    return originalBytes$.pipe(
      switchMap((originalBytes) =>
        this.qpdfWasmService.reprocessPdf(originalBytes)
      ),
      switchMap((reprocessedBytes) =>
        from(
          PDFDocument.load(reprocessedBytes, { ignoreEncryption: true })
        ).pipe(
          catchError((error) => {
            this.logger.error('Failed to load reprocessed document.', error);
            // Return the original workload since reprocessing failed.
            return of(workload);
          }),
          map((pdfOrWorkload) => {
            if (pdfOrWorkload instanceof PdfDocumentWorkload) {
              // It's a workload already, the original one due to an error.
              return pdfOrWorkload;
            }
            // Create a new workload with the reprocessed document.
            return new PdfDocumentWorkload(pdfOrWorkload);
          })
        )
      )
    );
  }

  /**
   * Load a `PDFDocument` by bytes and from a provided URL, including convert.
   *
   * @param arrayBuffer$ Observable `ArrayBuffer` of the PDF document.
   * @param url URL to load.
   * @returns An observable PDF document.
   */
  private loadAsPdf(arrayBuffer$: Observable<ArrayBuffer>, url: string) {
    // If document is not a PDF, but is a PDF convertable type, create a PDF.
    if (this.isUrlPdfConvertable(url)) {
      const fileType = this.getUrlPdfConvertableType(url);
      assertExists(fileType);
      return this.createNewPdfFromConvertable(arrayBuffer$, fileType);
    }

    // Return the retrieved PDF document.
    const pdfDocument$ = arrayBuffer$.pipe(
      catchError((error) => {
        this.logger.error(error);
        this.loadFailure.emit({ error });
        throw error;
      }),
      // Create a PDF document object from the fetched byte data.
      switchMap((originalPdfBytes) => {
        // Confirm the byte array contains a PDF.
        if (!PdfViewerComponent.isPdfArrayBuffer(originalPdfBytes)) {
          // Document is not supported.
          this.logger.error(
            'Document buffer was not convertable client-side and does not contain a PDF.'
          );
        }
        return from(
          PDFDocument.load(originalPdfBytes, { ignoreEncryption: true })
        ).pipe(
          catchError((error) => {
            // Notify the user so there is no question about whether or not the document is going to load.
            this.logger.error('Failed to load document.', error);
            this.notify.error({
              i18n: 'FAILED_TO_LOAD_DOCUMENT',
              error,
              description: 'Failed to load document.',
            });

            /*
            Return a new PDF with an error message in it so that the rest of the load process can complete.
            This allows the viewer to display other documents if the user clicks next/previous.
            */
            return from(PDFDocument.create()).pipe(
              tap((pdf) => {
                const page = pdf.addPage([600, 90]);
                // This PDF can not be displayed, it may be encrypted or corrupted.
                const translationKey = this.translate.translate(
                  'ERROR_ENCRYPTED_OR_CORRUPTED'
                );
                page.drawText(translationKey, {
                  size: 20,
                  x: 30,
                  y: 48,
                  maxWidth: 540,
                });
              })
            );
          })
        );
      })
    );
    // Return a new PDF document workload.
    return pdfDocument$.pipe(map((result) => new PdfDocumentWorkload(result)));
  }

  private getBytesFromUrl(url: string) {
    return this.httpClient.get(url, {
      responseType: 'arraybuffer',
    });
  }

  /**
   * Attempts to reset the viewer library from seeing a document as modified.
   *
   * This prevents errors/popups when changing URL in states like modified
   * AcroForm data etc.
   *
   * @returns {void} No return value.
   */
  private resetViewerModifiedState(): void {
    try {
      // Call into the iFrame's pdfdocument annotation storage to reset the modified state, if it exists.
      this.pdfJsComponent?.iframe?.nativeElement?.contentWindow?.PDFViewerApplication?.pdfDocument?.annotationStorage?.resetModified();
    } catch {
      // Do nothing.
    }
  }

  /**
   * Restrict the size of TIFF output using a scanvas to the max bitmap size.
   *
   * @param tiffFrame TIFF frame.
   * @param canvas Source canvas.
   * @param maxSize Size in pixels to restrict the square to.
   * @returns A new canvas scaled if required, otherwise the original canvas, and the scale applied.
   */
  private restrictCanvasToMaxSize(
    tiffFrame: utif.Ifd,
    canvas: HTMLCanvasElement,
    maxSize: number = 1200
  ): {
    /** Scale factor. */
    scale: number;
    /** Scaled canvas. */
    scaledCanvas: HTMLCanvasElement;
  } {
    let scale = 1;
    if (tiffFrame.width > maxSize || tiffFrame.height > maxSize) {
      const scaledCanvas = document.createElement('canvas');
      // Scale canvas with aspect ratio.
      scaledCanvas.width =
        tiffFrame.width > maxSize && tiffFrame.width > tiffFrame.height
          ? maxSize
          : tiffFrame.width * (maxSize / tiffFrame.height);
      scaledCanvas.height =
        tiffFrame.height > maxSize && tiffFrame.height > tiffFrame.width
          ? maxSize
          : tiffFrame.height * (maxSize / tiffFrame.width);
      this.logger.debug(
        `Large TIFF image, scaling output size to w:${scaledCanvas.width} x h:${scaledCanvas.height}.`
      );
      const scaledContext = scaledCanvas.getContext('2d');
      // Add the source canvas as an image at the new scale to the new canvas.
      scaledContext?.drawImage(
        canvas,
        0,
        0,
        scaledCanvas.width,
        scaledCanvas.height
      );
      scale = scaledCanvas.height / canvas.height;
      canvas = scaledCanvas;
      this.logger.debug('Image scaled to fit size restriction, factor:', scale);
    }
    return { scaledCanvas: canvas, scale };
  }

  /**
   * Save the PDF document workload to a byte array.
   *
   * @param workload Workload.
   * @returns Byte array.
   */
  private saveWorkloadToBytes(
    workload: PdfDocumentWorkload
  ): Observable<Uint8Array> {
    // Ensure page count is set.
    this.pdfViewerService.pageCount = workload.document.getPageCount();
    // Serialize the PDFDocument to data URL.
    return from(workload.document.save()).pipe(
      tap((pdfBytes) => {
        this.pdfBytes = pdfBytes;
        this.thumbnails = [];
        defer(
          () => pdfjs.getDocument(new Uint8Array(this.pdfBytes)).promise
        ).subscribe((pdfDocumentProxy) => {
          for (
            let index = 1, count = pdfDocumentProxy.numPages;
            index <= count;
            index++
          ) {
            const hasAnnotations = this.documentAnnotations.some(
              (annotation) => annotation.page === index - 1
            );
            this.thumbnails.push(
              new PdfThumbnail(index, hasAnnotations, pdfDocumentProxy)
            );
          }
        });
      })
    );
  }

  private setIsDocumentLoading(isLoading: boolean) {
    this.isDocumentLoading = isLoading;
    this.documentLoadingChange.emit(isLoading);
  }
}
